import { AlertTriangle, ShieldCheck, ListChecks } from "lucide-react";
import SurvivalTimeline from "../SurvivalTimeline";
import IndustrySpotlight from "../IndustrySpotlight";
import ProSectionGate from "../ProSectionGate";
import TopFixesCallout from "./TopFixesCallout";
import ImprovementIdeasGrid from "./ImprovementIdeasGrid";
import FindingsCard from "./FindingsCard";
import BankerQuestionsCard from "./BankerQuestionsCard";
import ScoreLeversCard from "./ScoreLeversCard";
import BenchmarkCommentaryCard from "./BenchmarkCommentaryCard";
import ValuationRangeCard from "./ValuationRangeCard";
import ConsequenceFlowCard from "./ConsequenceFlowCard";
import PeriodDeltaCard from "./PeriodDeltaCard";
import { SectionTitle, SectionMarker } from "./Headings";

/**
 * The full Insights block of the report — composes all 12 content sections
 * that share the same Pro-gating contract:
 *
 *   FREE for all:
 *     - Distress signals (score-capping flags)
 *     - Period-over-period delta (only if user has saved prior periods)
 *
 *   PRO-gated:
 *     1. Survival Timeline       (runway + deadlines)
 *     2. Top 3 Things to Fix     (prescriptive callout)
 *     3. Findings                (Strengths / Risks / Actions)
 *     4. Score Levers            (3 numbers that move the grade most)
 *     5. Banker Questions        (what a lender would ask)
 *     6. Industry KPI Spotlight  (sector-specific benchmarks)
 *     7. Benchmark Commentary    (peer comparison in narrative form)
 *     8. Valuation Range         (lender + buyer views, dollar estimates)
 *     9. Consequence Flow        (cost of inaction over 30d/90d/6m/12m)
 *    10. Improvement Ideas       (action plan cards with check-off)
 */
export default function InsightsSection({
  report,
  health,
  findings,
  input,
  proActive,
  sampleMode = false,
  onUnlock,
}) {
  const h = health;
  const industryLabel = report.benchmark?.label || input.industry || "your industry";
  // Reusable marker shorthands. Both render to nothing when sampleMode is false
  // so paying users never see the labels.
  const paidMark = sampleMode ? <SectionMarker kind="paid" /> : null;
  const freeMark = sampleMode ? <SectionMarker kind="free" /> : null;

  return (
    <>
      <SectionTitle eyebrow="Insights" title="Where to focus, what to fix, what to ship." />

      {/* FREE — period-over-period delta only appears if user has saved prior periods */}
      {report.period_delta && freeMark}
      <PeriodDeltaCard report={report} />

      {/* 1. Survival Timeline */}
      {report.survival_timeline && (
        <div className="mb-6">
          {proActive ? (
            <>
              {paidMark}
              <SurvivalTimeline timeline={report.survival_timeline} />
            </>
          ) : (
            <ProSectionGate
              testid="gate-survival"
              title="See your runway &amp; survival timeline"
              subtitle="How long the business can survive at current burn — with concrete deadlines."
              onUnlock={onUnlock}
            >
              <SurvivalTimeline timeline={report.survival_timeline} />
            </ProSectionGate>
          )}
        </div>
      )}

      {/* 2. Top 3 Things to Fix */}
      {report.top_fixes && report.top_fixes.length > 0 && (
        proActive ? (
          <>
            {paidMark}
            <TopFixesCallout fixes={report.top_fixes} />
          </>
        ) : (
          <ProSectionGate
            testid="gate-top-fixes"
            title="See the top 3 things to fix right now"
            subtitle="Each one quotes your actual numbers and pairs it with a concrete fix + impact math."
            onUnlock={onUnlock}
          >
            <TopFixesCallout fixes={report.top_fixes} />
          </ProSectionGate>
        )
      )}

      {/* FREE — Distress signals (score-capping — users MUST see these) */}
      {h.distress_flags && h.distress_flags.length > 0 && (
        <>
          {freeMark}
          <div
            className="mb-6 p-5 rounded-xl border border-[#A84632]/30 bg-[#A84632]/5"
            data-testid="distress-flags-callout"
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#A84632] mb-2">
              <AlertTriangle size={14} strokeWidth={1.5} />
              Distress signals capping your score
            </div>
            <ul className="space-y-1.5 text-sm text-[#1A1A1A] leading-relaxed">
              {h.distress_flags.map((f) => (
                <li key={f.key} className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#A84632] shrink-0" />
                  <span>{f.message}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-[#5C665D] mt-3 leading-relaxed">
              These flags hard-cap the overall score so a borderline-stressed business can&apos;t show up
              as &ldquo;Strong&rdquo; just because its other ratios look fine.
            </p>
          </div>
        </>
      )}

      {/* 3. Findings (Strengths / Risks / Actions) */}
      {paidMark}
      <FindingsTriad findings={findings} proActive={proActive} onUnlock={onUnlock} />

      {/* 4. Score Levers — sensitivity analysis */}
      {report.score_levers && report.score_levers.length > 0 && (
        proActive ? (
          <>
            {paidMark}
            <ScoreLeversCard levers={report.score_levers} />
          </>
        ) : (
          <ProSectionGate
            testid="gate-score-levers"
            title="See the 3 numbers that would move your score most"
            subtitle="Pre-baked sensitivity from the What-If sandbox — what each lever moves your grade to."
            onUnlock={onUnlock}
          >
            <ScoreLeversCard levers={report.score_levers} />
          </ProSectionGate>
        )
      )}

      {/* 5. Banker Questions */}
      {report.banker_questions && report.banker_questions.length > 0 && (
        proActive ? (
          <>
            {paidMark}
            <BankerQuestionsCard questions={report.banker_questions} />
          </>
        ) : (
          <ProSectionGate
            testid="gate-banker-questions"
            title="See what a banker would ask"
            subtitle="6-8 questions a senior commercial lender would raise about these numbers — rehearse before any meeting."
            onUnlock={onUnlock}
          >
            <BankerQuestionsCard questions={report.banker_questions} />
          </ProSectionGate>
        )
      )}

      {/* 6. Industry KPI Spotlight */}
      {report.industry_kpis && report.industry_kpis.length > 0 && (
        <>
          <SectionTitle
            eyebrow="Industry-specific"
            title="What matters most for your sector."
            proOnly
            sampleMode={sampleMode}
          />
          {proActive ? (
            <IndustrySpotlight
              kpis={report.industry_kpis}
              industryLabel={industryLabel}
              currency={input.currency}
            />
          ) : (
            <ProSectionGate
              testid="gate-industry"
              title="Unlock your industry KPI spotlight"
              subtitle={`Sector-specific benchmarks for ${industryLabel}.`}
              onUnlock={onUnlock}
            >
              <IndustrySpotlight
                kpis={report.industry_kpis}
                industryLabel={industryLabel}
                currency={input.currency}
              />
            </ProSectionGate>
          )}
        </>
      )}

      {/* 7. Benchmark Commentary */}
      {report.benchmark_commentary && (
        proActive ? (
          <>
            {paidMark}
            <BenchmarkCommentaryCard
              commentary={report.benchmark_commentary}
              industryLabel={industryLabel}
            />
          </>
        ) : (
          <ProSectionGate
            testid="gate-benchmark-commentary"
            title="See what the peer data means in plain English"
            subtitle="3-paragraph interpretation of profitability, leverage, and working capital vs your peer set."
            onUnlock={onUnlock}
          >
            <BenchmarkCommentaryCard
              commentary={report.benchmark_commentary}
              industryLabel={industryLabel}
            />
          </ProSectionGate>
        )
      )}

      {/* 8. Valuation Range */}
      {report.valuation_range && (
        proActive ? (
          <>
            {paidMark}
            <ValuationRangeCard
              valuation={report.valuation_range}
              currentEbitdaMargin={report.metrics?.ebitda_margin}
              revenue={report.metrics?._aggregates?.revenue}
              proActive
            />
          </>
        ) : (
          <ProSectionGate
            testid="gate-valuation"
            title="See your risk-adjusted valuation range"
            subtitle="What a lender would underwrite against vs what a buyer might pay — directional dollar estimates."
            onUnlock={onUnlock}
          >
            <ValuationRangeCard valuation={report.valuation_range} />
          </ProSectionGate>
        )
      )}

      {/* 9. Consequence Flow */}
      {report.consequence_flow && report.consequence_flow.length > 0 && (
        proActive ? (
          <>
            {paidMark}
            <ConsequenceFlowCard flow={report.consequence_flow} />
          </>
        ) : (
          <ProSectionGate
            testid="gate-consequence-flow"
            title="See what happens if you skip the action plan"
            subtitle="Sequential consequences of inaction across 30 days / 90 days / 6 months / 12 months."
            onUnlock={onUnlock}
          >
            <ConsequenceFlowCard flow={report.consequence_flow} />
          </ProSectionGate>
        )
      )}

      {/* 10. Improvement Ideas (with Action Plan check-off) */}
      {report.improvement_ideas && report.improvement_ideas.length > 0 && (
        <>
          <SectionTitle
            eyebrow="Improvement Ideas"
            title="Specific moves that change the numbers."
            proOnly
            sampleMode={sampleMode}
          />
          {proActive ? (
            <ImprovementIdeasGrid ideas={report.improvement_ideas} reportId={report.id} />
          ) : (
            <ProSectionGate
              testid="gate-ideas"
              title="See all improvement ideas with dollar impact"
              subtitle="Each prioritized, timelined, and quantified in your currency."
              onUnlock={onUnlock}
            >
              <ImprovementIdeasGrid ideas={report.improvement_ideas} reportId={report.id} />
            </ProSectionGate>
          )}
        </>
      )}
    </>
  );
}

/**
 * Three-up Strengths / Risks / Action Items. Pulled into its own component
 * because the Pro-gated copy wraps the same triad twice, and DRY > clever.
 */
function FindingsTriad({ findings, proActive, onUnlock }) {
  const cards = (suffix = "") => (
    <div className="grid lg:grid-cols-3 gap-6">
      <FindingsCard
        icon={ShieldCheck}
        title="Strengths"
        tone="success"
        items={findings.strengths || []}
        testid={`findings-strengths${suffix}`}
      />
      <FindingsCard
        icon={AlertTriangle}
        title="Material risks"
        tone="danger"
        items={findings.risks || []}
        testid={`findings-risks${suffix}`}
      />
      <FindingsCard
        icon={ListChecks}
        title="Action items"
        tone="accent"
        items={findings.actions || []}
        testid={`findings-actions${suffix}`}
      />
    </div>
  );

  if (proActive) return cards("");
  return (
    <ProSectionGate
      testid="gate-findings"
      title="See your full Strengths, Risks &amp; Action Items"
      subtitle="Every item quantified in your currency, tied to your industry benchmark."
      onUnlock={onUnlock}
    >
      {cards("-preview")}
    </ProSectionGate>
  );
}
